import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  advanceAuditExport,
  deleteAuditView,
  downloadAuditExport,
  getAuditEntryDetail,
  getAuditFilterOptions,
  listAuditExports,
  listAuditViews,
  saveAuditView,
  searchAuditLog,
  startAuditExport,
} from "@/lib/email-admin.functions";

export const Route = createFileRoute("/_authenticated/admin/audit")({
  head: () => ({
    meta: [
      { title: "Audit log — Quibblies staff" },
      {
        name: "description",
        content:
          "Search every staff action on Quibblies — resend attempts, template test runs and decisions — by notice type, wording version, person and date.",
      },
      { property: "og:title", content: "Audit log — Quibblies staff" },
      {
        property: "og:description",
        content: "Filter resend attempts and test runs by notice type, version, user and time.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AuditLogPage,
});

const selectClass =
  "h-10 w-full rounded-md border border-border/60 bg-background px-3 text-sm text-foreground";

/** Shortcuts for the two things staff look for most often. */
const PRESETS: Array<{ label: string; actions: string[] }> = [
  { label: "Everything", actions: [] },
  {
    label: "Resend attempts",
    actions: ["notice.manual_resend", "notice.manual_resend_batch", "notice.retried"],
  },
  { label: "Template test runs", actions: ["email_template.test_sent"] },
  {
    label: "Wording changes",
    actions: ["email_template.updated", "email_template.reset", "email_template.restored"],
  },
  { label: "Webhook replays", actions: ["email_webhook.replayed"] },
];

type ExportJob = Awaited<ReturnType<typeof startAuditExport>>;

function AuditLogPage() {
  const search = useServerFn(searchAuditLog);
  const loadOptions = useServerFn(getAuditFilterOptions);
  const beginExport = useServerFn(startAuditExport);
  const advanceExport = useServerFn(advanceAuditExport);
  const fetchExport = useServerFn(downloadAuditExport);
  const loadExports = useServerFn(listAuditExports);
  const loadDetail = useServerFn(getAuditEntryDetail);
  const loadViews = useServerFn(listAuditViews);
  const saveView = useServerFn(saveAuditView);
  const removeView = useServerFn(deleteAuditView);
  const queryClient = useQueryClient();
  const [viewName, setViewName] = useState("");
  const [shareView, setShareView] = useState(false);
  const [activeView, setActiveView] = useState("");

  const [preset, setPreset] = useState(0);
  const [action, setAction] = useState("");
  const [template, setTemplate] = useState("");
  const [templateVersionId, setTemplateVersionId] = useState("");
  const [actorId, setActorId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [text, setText] = useState("");

  const { data: options } = useQuery({
    queryKey: ["audit-filter-options"],
    queryFn: () => loadOptions(),
    retry: false,
  });

  const presetActions = PRESETS[preset]?.actions ?? [];
  const actions = action ? [action] : presetActions;

  const filters = { actions, template, templateVersionId, actorId, from, to, search: text };

  const { data: views } = useQuery({
    queryKey: ["audit-views"],
    queryFn: () => loadViews(),
    retry: false,
  });

  // Background export: the job is built a chunk at a time so a very large
  // download never has to finish inside one request.
  const [job, setJob] = useState<ExportJob | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const working = useRef(false);

  const { data: exports } = useQuery({
    queryKey: ["audit-exports"],
    queryFn: () => loadExports(),
    retry: false,
  });

  function saveFile(filename: string, csv: string) {
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  const startExportMutation = useMutation({
    mutationFn: () => beginExport({ data: { ...filters, notify: true } }),
    onSuccess: (created) => {
      setJob(created);
      toast.success("Export started", {
        description: "It keeps running while you work — we'll email you when the file is ready.",
      });
    },
    onError: (e: Error) => toast.error("Could not start that export", { description: e.message }),
  });

  const downloadMutation = useMutation({
    mutationFn: (jobId: string) => fetchExport({ data: { jobId } }),
    onSuccess: (file) => {
      saveFile(file.filename, file.csv);
      toast.success(`Downloaded ${file.count} entries`);
    },
    onError: (e: Error) => toast.error("Download failed", { description: e.message }),
  });

  useEffect(() => {
    if (!job || job.status === "complete" || job.status === "failed") return;
    let cancelled = false;
    const timer = setInterval(async () => {
      if (working.current) return;
      working.current = true;
      try {
        const next = await advanceExport({ data: { jobId: job.id } });
        if (cancelled) return;
        setJob(next);
        if (next.status === "complete") {
          toast.success(`Export ready — ${next.processed} entries`);
          void queryClient.invalidateQueries({ queryKey: ["audit-exports"] });
        } else if (next.status === "failed") {
          toast.error("Export failed", { description: next.error ?? undefined });
        }
      } catch (error) {
        if (!cancelled) {
          toast.error("Export failed", {
            description: error instanceof Error ? error.message : undefined,
          });
          setJob((current) => (current ? { ...current, status: "failed" } : current));
        }
      } finally {
        working.current = false;
      }
    }, 700);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [job, advanceExport, queryClient]);

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["audit-detail", detailId],
    queryFn: () => loadDetail({ data: { id: detailId as string } }),
    enabled: Boolean(detailId),
    retry: false,
  });

  const saveViewMutation = useMutation({
    mutationFn: () =>
      saveView({
        data: {
          name: viewName,
          filtersJson: JSON.stringify({ preset, action, ...filters }),
          shared: shareView,
        },
      }),
    onSuccess: () => {
      toast.success(`Saved “${viewName}”`);
      setViewName("");
      void queryClient.invalidateQueries({ queryKey: ["audit-views"] });
    },
    onError: (e: Error) => toast.error("Could not save that view", { description: e.message }),
  });

  const deleteViewMutation = useMutation({
    mutationFn: (id: string) => removeView({ data: { id } }),
    onSuccess: () => {
      setActiveView("");
      void queryClient.invalidateQueries({ queryKey: ["audit-views"] });
    },
    onError: (e: Error) => toast.error("Could not delete that view", { description: e.message }),
  });

  /** Puts every control back to the state that was saved under this name. */
  function applyView(id: string) {
    const view = (views ?? []).find((row) => row.id === id);
    if (!view) return;
    setActiveView(id);
    try {
      const saved = JSON.parse(view.filtersJson) as Record<string, unknown>;
      setPreset(typeof saved["preset"] === "number" ? (saved["preset"] as number) : 0);
      setAction(typeof saved["action"] === "string" ? (saved["action"] as string) : "");
      setTemplate(typeof saved["template"] === "string" ? (saved["template"] as string) : "");
      setTemplateVersionId(
        typeof saved["templateVersionId"] === "string" ? (saved["templateVersionId"] as string) : "",
      );
      setActorId(typeof saved["actorId"] === "string" ? (saved["actorId"] as string) : "");
      setFrom(typeof saved["from"] === "string" ? (saved["from"] as string) : "");
      setTo(typeof saved["to"] === "string" ? (saved["to"] as string) : "");
      setText(typeof saved["search"] === "string" ? (saved["search"] as string) : "");
    } catch {
      toast.error("That saved view could not be read");
    }
  }
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["audit-log", filters],
    queryFn: () => search({ data: filters }),
    retry: false,
  });

  function clear() {
    setPreset(0);
    setAction("");
    setTemplate("");
    setTemplateVersionId("");
    setActorId("");
    setFrom("");
    setTo("");
    setText("");
  }

  const versionOptions = (options?.versions ?? []).filter(
    (version) => !template || version.template === template,
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Audit log"
        description="Every recorded staff action, append-only. Narrow it down to the resend attempts or test runs you are looking for by notice type, wording version, person and date."
      />

      <div className="mt-8 flex flex-wrap gap-2">
        {PRESETS.map((item, index) => (
          <Button
            key={item.label}
            size="sm"
            variant={index === preset && !action ? "default" : "soft"}
            onClick={() => {
              setPreset(index);
              setAction("");
            }}
          >
            {item.label}
          </Button>
        ))}
      </div>

      <section className="mt-4 grid gap-3 rounded-2xl border border-border/60 bg-surface/40 p-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1 lg:col-span-3">
          <Label htmlFor="audit-search">Search</Label>
          <Input
            id="audit-search"
            placeholder="Recipient, notice type, outcome, anything recorded with the action"
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-action">Action</Label>
          <select
            id="audit-action"
            className={selectClass}
            value={action}
            onChange={(event) => setAction(event.target.value)}
          >
            <option value="">Any action in this preset</option>
            {(options?.actions ?? []).map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-template">Notice type</Label>
          <select
            id="audit-template"
            className={selectClass}
            value={template}
            onChange={(event) => {
              setTemplate(event.target.value);
              setTemplateVersionId("");
            }}
          >
            <option value="">Any notice type</option>
            {(options?.templates ?? []).map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-version">Wording version</Label>
          <select
            id="audit-version"
            className={selectClass}
            value={templateVersionId}
            onChange={(event) => setTemplateVersionId(event.target.value)}
          >
            <option value="">Any version</option>
            {versionOptions.map((version) => (
              <option key={version.id} value={version.id}>
                {version.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-actor">Person</Label>
          <select
            id="audit-actor"
            className={selectClass}
            value={actorId}
            onChange={(event) => setActorId(event.target.value)}
          >
            <option value="">Anyone</option>
            {(options?.actors ?? []).map((actor) => (
              <option key={actor.id} value={actor.id}>
                {actor.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-from">From</Label>
          <Input
            id="audit-from"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-to">To</Label>
          <Input
            id="audit-to"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-end gap-2 lg:col-span-3">
          <Button variant="ghost" size="sm" onClick={clear}>
            Clear filters
          </Button>
          <Button
            variant="soft"
            size="sm"
            onClick={() => startExportMutation.mutate()}
            disabled={
              startExportMutation.isPending ||
              Boolean(job && job.status !== "complete" && job.status !== "failed")
            }
          >
            {startExportMutation.isPending ? "Starting…" : "Export CSV in background"}
          </Button>
        </div>
      </section>

      {job ? (
        <section className="mt-4 rounded-2xl border border-border/60 bg-surface/40 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-lg text-foreground">Export in progress</h2>
            <Badge variant="outline">{job.status}</Badge>
          </div>
          <Progress
            className="mt-3"
            value={
              job.status === "complete"
                ? 100
                : Math.min(
                    97,
                    job.estimatedTotal ? (job.processed / job.estimatedTotal) * 100 : 5,
                  )
            }
          />
          <p className="mt-2 text-sm text-muted-foreground" aria-live="polite">
            {job.status === "complete"
              ? `Finished — ${job.processed} entries ready to download.${job.notified ? " We emailed you as well." : ""}`
              : job.status === "failed"
                ? (job.error ?? "The export could not be completed.")
                : `Preparing… ${job.processed} of about ${job.estimatedTotal || "?"} entries written. You can keep working; we'll email you when it's ready.`}
          </p>
          {job.status === "complete" ? (
            <Button
              className="mt-3"
              size="sm"
              variant="soft"
              onClick={() => downloadMutation.mutate(job.id)}
              disabled={downloadMutation.isPending}
            >
              Download {job.filename}
            </Button>
          ) : null}
        </section>
      ) : null}

      {(exports ?? []).length ? (
        <section className="mt-4 rounded-2xl border border-border/60 bg-surface/40 p-5">
          <h2 className="font-display text-lg text-foreground">Recent exports</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {(exports ?? []).map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-3">
                <span className="text-muted-foreground">
                  {new Date(row.createdAt).toLocaleString()}
                </span>
                <Badge variant="outline">{row.status}</Badge>
                <span className="text-muted-foreground">{row.processed} entries</span>
                {row.status === "complete" ? (
                  <Button size="sm" variant="ghost" onClick={() => downloadMutation.mutate(row.id)}>
                    Download
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}


      <section className="mt-4 rounded-2xl border border-border/60 bg-surface/40 p-5">
        <h2 className="font-display text-lg text-foreground">Saved views</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Keep the filter sets you use often and switch between them in one click. Views are
          yours alone unless you share them with the rest of the team.
        </p>

        {(views ?? []).length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {(views ?? []).map((view) => (
              <span
                key={view.id}
                className={`flex items-center gap-1 rounded-full border px-1 ${
                  activeView === view.id ? "border-primary bg-primary/10" : "border-border/60"
                }`}
              >
                <Button size="sm" variant="ghost" onClick={() => applyView(view.id)}>
                  {view.name}
                  {view.shared ? (
                    <Badge variant="outline" className="ml-2">
                      {view.mine ? "shared" : "team"}
                    </Badge>
                  ) : null}
                </Button>
                {view.mine ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Delete saved view ${view.name}`}
                    onClick={() => deleteViewMutation.mutate(view.id)}
                  >
                    ×
                  </Button>
                ) : null}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">No saved views yet.</p>
        )}

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="view-name">Save these filters as</Label>
            <Input
              id="view-name"
              placeholder="e.g. Failed resends this week"
              value={viewName}
              onChange={(event) => setViewName(event.target.value)}
              className="w-64"
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={shareView}
              onChange={(event) => setShareView(event.target.checked)}
            />
            Share with the team
          </label>
          <Button
            size="sm"
            variant="soft"
            onClick={() => saveViewMutation.mutate()}
            disabled={!viewName.trim() || saveViewMutation.isPending}
          >
            Save view
          </Button>
        </div>
      </section>

      {error ? (
        <p className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
          {error.message}
        </p>
      ) : isLoading ? (
        <Skeleton className="mt-8 h-72 w-full rounded-2xl" />
      ) : (
        <>
          <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
            {isFetching ? "Searching…" : `${data?.length ?? 0} matching entries`}
          </p>
          <div className="mt-3 overflow-x-auto rounded-2xl border border-border/60">
            <table className="w-full min-w-[880px] text-left text-sm">
              <caption className="sr-only">Filtered audit log entries</caption>
              <thead className="bg-surface/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Person</th>
                  <th className="px-4 py-3">Notice type</th>
                  <th className="px-4 py-3">Details</th>
                  <th className="px-4 py-3 sr-only">Open</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((row) => (
                  <tr key={row.id} className="border-t border-border/50 align-top">
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{row.action}</Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {row.actorName ?? "System"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{row.template ?? "—"}</td>
                    <td className="px-4 py-3">
                      <p className="max-w-[28rem] break-words font-mono text-xs text-muted-foreground">
                        {row.metadataJson === "{}" ? "—" : row.metadataJson}
                      </p>
                      {row.templateVersionId ? (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          wording version {row.templateVersionId.slice(0, 8)}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="ghost" onClick={() => setDetailId(row.id)}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
                {(data ?? []).length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-muted-foreground" colSpan={6}>
                      Nothing matches those filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Sheet open={Boolean(detailId)} onOpenChange={(open) => !open && setDetailId(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{detail?.action ?? "Entry details"}</SheetTitle>
            <SheetDescription>
              Everything recorded with this action, including the raw provider payloads.
            </SheetDescription>
          </SheetHeader>

          {detailLoading ? (
            <Skeleton className="mt-6 h-64 w-full rounded-xl" />
          ) : detail ? (
            <div className="mt-6 space-y-6 text-sm">
              <dl className="grid grid-cols-2 gap-2">
                <dt className="text-muted-foreground">When</dt>
                <dd>{new Date(detail.createdAt).toLocaleString()}</dd>
                <dt className="text-muted-foreground">Person</dt>
                <dd>{detail.actorName ?? "System"}</dd>
                <dt className="text-muted-foreground">Entity</dt>
                <dd className="break-all">
                  {detail.entityType ?? "—"} {detail.entityId ? `· ${detail.entityId}` : ""}
                </dd>
              </dl>

              <div>
                <h3 className="font-display text-base text-foreground">Recorded details</h3>
                <pre className="mt-2 overflow-x-auto rounded-xl border border-border/60 bg-surface/60 p-3 font-mono text-xs">
                  {detail.metadataJson}
                </pre>
              </div>

              {detail.notice ? (
                <div>
                  <h3 className="font-display text-base text-foreground">Notice</h3>
                  <dl className="mt-2 grid grid-cols-2 gap-2">
                    <dt className="text-muted-foreground">Recipient</dt>
                    <dd className="break-all">{detail.notice.email}</dd>
                    <dt className="text-muted-foreground">Notice type</dt>
                    <dd>{detail.notice.template}</dd>
                    <dt className="text-muted-foreground">Subject</dt>
                    <dd>{detail.notice.subject}</dd>
                    <dt className="text-muted-foreground">Delivery</dt>
                    <dd>
                      {detail.notice.deliveryStatus} · {detail.notice.attempts} attempt
                      {detail.notice.attempts === 1 ? "" : "s"}
                    </dd>
                    <dt className="text-muted-foreground">Provider message id</dt>
                    <dd className="break-all">{detail.notice.providerMessageId ?? "—"}</dd>
                    <dt className="text-muted-foreground">Last failure</dt>
                    <dd>{detail.notice.failureReason ?? "—"}</dd>
                  </dl>
                </div>
              ) : null}

              <div>
                <h3 className="font-display text-base text-foreground">Provider events</h3>
                {detail.providerEvents.length ? (
                  <ul className="mt-2 space-y-3">
                    {detail.providerEvents.map((event) => (
                      <li key={event.id} className="rounded-xl border border-border/60 p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">{event.eventType}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(event.receivedAt).toLocaleString()} · via {event.ingestSource}
                          </span>
                        </div>
                        <p className="mt-1 break-all text-xs text-muted-foreground">
                          {event.recipient} · event {event.providerEventId}
                          {event.messageId ? ` · message ${event.messageId}` : ""}
                        </p>
                        {event.reason ? <p className="mt-1 text-xs">{event.reason}</p> : null}
                        <pre className="mt-2 overflow-x-auto rounded-lg bg-surface/60 p-2 font-mono text-[11px]">
                          {event.payloadJson}
                        </pre>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-muted-foreground">
                    No provider events are linked to this entry.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="mt-6 text-sm text-muted-foreground">Nothing to show for this entry.</p>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
